// pages/GuildMemberManagement.js (완전한 코드 - 분할 가능)
// 기존 import에 추가
import GuildLogo from "./GuildLogo";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp
} from "firebase/firestore";
import { guildDataService } from "./GuildDataService";

// 길드 등급 상수
const GUILD_TIERS = ["길드장", "부길드장", "정회원", "임시회원"];

// 부길드장 역할 옵션
const SUB_LEADER_ROLES = [
  "운동용품 담당",
  "회계 담당", 
  "일정 관리",
  "신규 멤버 담당",
  "대외 활동 담당",
  "교육 담당",
  "홍보 담당"
];

function GuildMemberManagement() {
  const { guildId } = useParams();
  const navigate = useNavigate();
  const localUser = JSON.parse(localStorage.getItem("user"));

  // 상태 관리
  const [guild, setGuild] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedTier, setSelectedTier] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("tier");
  const [filterBy, setFilterBy] = useState("all");
  const [showBulkNotification, setShowBulkNotification] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showMemberDetail, setShowMemberDetail] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const MEMBERS_PER_PAGE = 12;

  // 권한 체크
  const isLeader = guild?.leaderId === localUser?.id;
  const currentMember = members.find(m => m.id === localUser?.id);
  const isSubLeader = currentMember?.guildTier === "부길드장";
  const canManage = isLeader;

  useEffect(() => {
    if (guildId) {
      loadGuildData();
    } else {
      navigate("/guild");
    }
  }, [guildId]);

  const loadGuildData = async () => {
    setLoading(true);
    try {
      const guildData = await guildDataService.getMyGuild(localUser.id);
      if (guildData && guildData.guild.id === guildId) {
        setGuild(guildData.guild);
        setMembers(guildData.members);
      } else {
        navigate("/guild");
      }
    } catch (error) {
      console.error("길드 데이터 로드 실패:", error);
      navigate("/guild");
    } finally {
      setLoading(false);
    }
  };

  const handleTierChange = async (memberId, newTier, newRole = "") => {
    if (!canManage) {
      alert("권한이 없습니다.");
      return;
    }

    try {
      await guildDataService.updateMemberTier(guildId, memberId, newTier, newRole);
      
      setMembers(prev => prev.map(member => 
        member.id === memberId 
          ? { ...member, guildTier: newTier, guildRole: newRole }
          : member
      ));

      const member = members.find(m => m.id === memberId);
      await addDoc(collection(db, "notifications"), {
        type: "tier_change",
        from: localUser.id,
        to: member.id,
        guildId: guildId,
        guildName: guild.name,
        newTier: newTier,
        newRole: newRole,
        status: "pending",
        createdAt: serverTimestamp(),
        message: `${guild.name} 길드에서 등급이 "${newTier}"${newRole ? ` (${newRole})` : ""}로 변경되었습니다.`
      });

      setEditingMember(null);
      alert("멤버 등급이 변경되었습니다.");
    } catch (error) {
      console.error("등급 변경 실패:", error);
      alert("등급 변경에 실패했습니다.");
    }
  };

  const handleKickMember = async (member) => {
    if (!canManage) {
      alert("권한이 없습니다.");
      return;
    }

    if (member.guildTier === "길드장") {
      alert("길드장은 강제 탈퇴시킬 수 없습니다.");
      return;
    }

    if (!window.confirm(`정말로 ${member.nickname}님을 길드에서 강제 탈퇴시키겠습니까?`)) {
      return;
    }

    try {
      await updateDoc(doc(db, "users", member.id), {
        guildId: null,
        guildName: null,
        guildTier: null,
        guildRole: null
      });

      await updateDoc(doc(db, "guilds", guildId), {
        memberCount: guild.memberCount - 1
      });

      await addDoc(collection(db, "notifications"), {
        type: "guild_kick",
        from: localUser.id,
        to: member.id,
        guildId: guildId,
        guildName: guild.name,
        status: "pending",
        createdAt: serverTimestamp(),
        message: `${guild.name} 길드에서 강제 탈퇴되었습니다.`
      });

      setMembers(prev => prev.filter(m => m.id !== member.id));
      alert("멤버가 강제 탈퇴되었습니다.");
    } catch (error) {
      console.error("강제 탈퇴 실패:", error);
      alert("강제 탈퇴에 실패했습니다.");
    }
  };

  const sendBulkNotification = async () => {
    if (!bulkMessage.trim()) {
      alert("메시지를 입력해주세요.");
      return;
    }

    const targetMembers = selectedMembers.length > 0 ? 
      members.filter(m => selectedMembers.includes(m.id)) : 
      members;

    try {
      const notifications = targetMembers.map(member => ({
        type: "guild_announcement",
        from: localUser.id,
        to: member.id,
        guildId: guildId,
        guildName: guild.name,
        status: "pending",
        createdAt: serverTimestamp(),
        message: `[${guild.name}] ${bulkMessage}`
      }));

      const batch = [];
      for (const notification of notifications) {
        batch.push(addDoc(collection(db, "notifications"), notification));
      }
      await Promise.all(batch);

      setBulkMessage("");
      setShowBulkNotification(false);
      setSelectedMembers([]);
      alert(`${targetMembers.length}명의 멤버에게 알림이 전송되었습니다.`);
    } catch (error) {
      console.error("단체 알림 전송 실패:", error);
      alert("알림 전송에 실패했습니다.");
    }
  };

  // 유틸리티 함수들
  const getFilteredAndSortedMembers = () => {
    let filtered = members.filter(member => {
      const matchesSearch = member.nickname?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterBy === "all" || member.guildTier === filterBy;
      return matchesSearch && matchesFilter;
    });

    const tierOrder = { "길드장": 0, "부길드장": 1, "정회원": 2, "임시회원": 3 };
    
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "tier":
          return tierOrder[a.guildTier] - tierOrder[b.guildTier];
        case "contribution":
          return (b.contribution || 0) - (a.contribution || 0);
        case "lastLogin":
          const aDate = a.lastLoginDate ? a.lastLoginDate.toDate() : new Date(0);
          const bDate = b.lastLoginDate ? b.lastLoginDate.toDate() : new Date(0);
          return bDate - aDate;
        case "nickname":
          return a.nickname?.localeCompare(b.nickname || "") || 0;
        case "joinDate":
          const aJoin = a.joinedGuildAt ? a.joinedGuildAt.toDate() : new Date(0);
          const bJoin = b.joinedGuildAt ? b.joinedGuildAt.toDate() : new Date(0);
          return bJoin - aJoin;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case "길드장": return "#ff6b35";
      case "부길드장": return "#f7931e";
      case "정회원": return "#4ecdc4";
      case "임시회원": return "#95a5a6";
      default: return "#95a5a6";
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case "길드장": return "👑";
      case "부길드장": return "🔱";
      case "정회원": return "⭐";
      case "임시회원": return "📝";
      default: return "👤";
    }
  };

  const getOnlineStatus = (lastLogin) => {
    if (!lastLogin) return { status: "오프라인", color: "#95a5a6", dot: "⚫" };
    
    const now = new Date();
    const loginDate = lastLogin.toDate ? lastLogin.toDate() : new Date(lastLogin);
    const diff = now - loginDate;
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 5) return { status: "온라인", color: "#4ecdc4", dot: "🟢" };
    if (minutes < 60) return { status: `${minutes}분 전`, color: "#f7931e", dot: "🟡" };
    if (minutes < 1440) return { status: `${Math.floor(minutes/60)}시간 전`, color: "#ff6b35", dot: "🟠" };
    return { status: "오프라인", color: "#95a5a6", dot: "⚫" };
  };

  const formatLastLogin = (dateData) => {
    if (!dateData) return "정보 없음";
    
    try {
      let date;
      
      if (dateData && typeof dateData.toDate === 'function') {
        date = dateData.toDate();
      } else if (dateData instanceof Date) {
        date = dateData;
      } else if (dateData) {
        date = new Date(dateData);
      } else {
        return "정보 없음";
      }
      
      if (isNaN(date.getTime())) {
        return "정보 없음";
      }
      
      const now = new Date();
      const diff = now - date;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      
      if (days === 0) return "오늘";
      if (days === 1) return "어제";
      if (days < 7) return `${days}일 전`;
      return date.toLocaleDateString();
    } catch (error) {
      console.error("날짜 포맷팅 오류:", error, dateData);
      return "정보 없음";
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}></div>
        <div style={styles.loadingContent}>
          <p style={styles.loadingText}>멤버 정보를 불러오는 중...</p>
          <div style={styles.loadingSubtext}>잠시만 기다려주세요</div>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorCard}>
            <div style={styles.errorIcon}>🚫</div>
            <h2 style={styles.errorTitle}>접근 권한이 없습니다</h2>
            <p style={styles.errorDesc}>길드장만 이 페이지에 접근할 수 있습니다.</p>
            <button 
              onClick={() => navigate("/guild")} 
              style={styles.primaryButton}
            >
              길드 홈으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  const filteredMembers = getFilteredAndSortedMembers();
  const totalPages = Math.ceil(filteredMembers.length / MEMBERS_PER_PAGE);
  const currentMembers = filteredMembers.slice(
    (currentPage - 1) * MEMBERS_PER_PAGE,
    currentPage * MEMBERS_PER_PAGE
  );

  const tierStats = GUILD_TIERS.map(tier => ({
    tier,
    count: members.filter(m => m.guildTier === tier).length,
    icon: getTierIcon(tier),
    color: getTierColor(tier)
  }));

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button onClick={() => navigate("/guild")} style={styles.backButton}>
            ← 뒤로가기
          </button>
          <div style={styles.headerActions}>
            <button onClick={() => setShowBulkNotification(true)} style={styles.notificationButton}>
              📢 단체 알림
            </button>
          </div>
        </div>

        <div style={styles.headerContent}>
          <div style={styles.guildInfo}>
            <div style={styles.guildAvatar}>
              {guild?.logoUrl ? (
                <img src={guild.logoUrl} alt="길드 로고" style={styles.guildLogo} />
              ) : (
                <div style={styles.guildLogoPlaceholder}>🏰</div>
              )}
            </div>
            <div style={styles.guildDetails}>
              <h1 style={styles.title}>{guild?.name} 멤버 관리</h1>
              <div style={styles.guildMeta}>
                <span style={styles.guildSport}>🎯 {guild?.sport}</span>
                <span style={styles.guildRegion}>📍 {guild?.region}</span>
                <span style={styles.totalMembers}>👥 총 {members.length}명</span>
              </div>
            </div>
          </div>
        </div>

        {/* 등급별 통계 */}
        <div style={styles.statsContainer}>
          {tierStats.map(stat => (
            <div 
              key={stat.tier} 
              style={styles.statCard}
              onClick={() => setFilterBy(filterBy === stat.tier ? "all" : stat.tier)}
            >
              <div style={styles.statIcon}>{stat.icon}</div>
              <div style={styles.statContent}>
                <div style={styles.statNumber}>{stat.count}</div>
                <div style={styles.statLabel}>{stat.tier}</div>
              </div>
              <div style={{...styles.statIndicator, backgroundColor: stat.color}}></div>
            </div>
          ))}
        </div>
      </div>

      {/* 검색 및 필터 컨트롤 */}
      <div style={styles.controlsSection}>
        <div style={styles.searchSection}>
          <div style={styles.searchInputContainer}>
            <div style={styles.searchIcon}>🔍</div>
            <input
              type="text"
              placeholder="멤버 이름으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} style={styles.clearButton}>
                ✕
              </button>
            )}
          </div>
        </div>

        <div style={styles.filterSection}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>등급 필터:</label>
            <select value={filterBy} onChange={(e) => setFilterBy(e.target.value)} style={styles.filterSelect}>
              <option value="all">전체</option>
              {GUILD_TIERS.map(tier => (
                <option key={tier} value={tier}>{tier}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>정렬:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={styles.filterSelect}>
              <option value="tier">등급순</option>
              <option value="nickname">이름순</option>
              <option value="joinDate">가입일순</option>
              <option value="lastLogin">최근 접속순</option>
              <option value="contribution">기여도순</option>
            </select>
          </div>
        </div>
      </div>

      {/* 멤버 목록 */}
      <div style={styles.membersSection}>
        <div style={styles.membersHeader}>
          <div style={styles.resultsInfo}>
            {searchTerm || filterBy !== "all" ? (
              <span style={styles.resultsText}>
                검색 결과: {filteredMembers.length}명
                {searchTerm && ` (${searchTerm})`}
                {filterBy !== "all" && ` (${filterBy})`}
              </span>
            ) : (
              <span style={styles.resultsText}>전체 멤버: {members.length}명</span>
            )}
          </div>

          {selectedMembers.length > 0 && (
            <div style={styles.selectedInfo}>
              <span style={styles.selectedText}>{selectedMembers.length}명 선택됨</span>
              <button onClick={() => setSelectedMembers([])} style={styles.clearSelectionButton}>
                선택 해제
              </button>
            </div>
          )}
        </div>

        {filteredMembers.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>👥</div>
            <div style={styles.emptyTitle}>
              {searchTerm || filterBy !== "all" ? "검색 결과가 없습니다" : "멤버가 없습니다"}
            </div>
            <div style={styles.emptyDesc}>
              {searchTerm ? "다른 검색어를 시도해보세요" : "아직 가입한 멤버가 없습니다"}
            </div>
          </div>
        ) : (
          <>
            <div style={styles.membersList}>
              {currentMembers.map(member => {
                const onlineStatus = getOnlineStatus(member.lastLoginDate);
                const isSelected = selectedMembers.includes(member.id);
                
                return (
                  <div 
                    key={member.id} 
                    style={{
                      ...styles.memberCard,
                      ...(isSelected ? styles.selectedMemberCard : {})
                    }}
                    onClick={() => setShowMemberDetail(member)}
                  >
                    {/* 카드 헤더 */}
                    <div style={styles.memberCardHeader}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            setSelectedMembers(prev => [...prev, member.id]);
                          } else {
                            setSelectedMembers(prev => prev.filter(id => id !== member.id));
                          }
                        }}
                        style={styles.memberCheckbox}
                      />
                      <div style={styles.onlineStatus}>
                        <span style={{ color: onlineStatus.color }}>{onlineStatus.dot}</span>
                      </div>
                    </div>

                    {/* 프로필 섹션 */}
                    <div style={styles.memberProfile}>
                      <div style={styles.memberAvatarContainer}>
                        <img 
                          src={member.profileImg || "/default-avatar.png"}
                          alt="프로필"
                          style={styles.memberAvatar}
                        />
                        <div style={{
                          ...styles.tierBadgeSmall,
                          backgroundColor: getTierColor(member.guildTier)
                        }}>
                          {getTierIcon(member.guildTier)}
                        </div>
                      </div>
                      
                      <div style={styles.memberInfo}>
                        <div style={styles.memberName}>
                          {member.nickname}
                          {member.id === localUser.id && (
                            <span style={styles.myBadge}>나</span>
                          )}
                          {member.APTI && (
                            <span style={{
                              marginLeft: 8,
                              fontSize: 16,
                              background: '#f3f4f6',
                              borderRadius: '8px',
                              padding: '2px 8px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#374151',
                              fontWeight: 600
                            }}>
                              {member.APTI.icon || '🧠'} {member.APTI.type}
                            </span>
                          )}
                        </div>
                        
                        <div style={styles.memberTier}>
                          <span style={{
                            ...styles.tierBadge,
                            backgroundColor: getTierColor(member.guildTier)
                          }}>
                            {member.guildTier}
                          </span>
                          {member.guildRole && (
                            <span style={styles.roleBadge}>
                              {member.guildRole}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 멤버 통계 */}
                    <div style={styles.memberStats}>
                      <div style={styles.statItem}>
                        <div style={styles.statValue}>{member.contribution || 0}</div>
                        <div style={styles.statLabel}>기여도</div>
                      </div>
                      <div style={styles.statItem}>
                        <div style={styles.statValue}>
                          {member.guildWins || 0}승 {member.guildLosses || 0}패
                        </div>
                        <div style={styles.statLabel}>길드전</div>
                      </div>
                      <div style={styles.statItem}>
                        <div style={styles.statValue}>{formatLastLogin(member.lastLoginDate)}</div>
                        <div style={styles.statLabel}>최근 접속</div>
                      </div>
                    </div>

                    {/* 관리 버튼 */}
                    {canManage && member.guildTier !== "길드장" && member.id !== localUser.id && (
                      <div style={styles.memberActions}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingMember(member.id);
                            setSelectedTier(member.guildTier);
                            setSelectedRole(member.guildRole || "");
                          }}
                          style={styles.editButton}
                        >
                          ⚙️ 관리
                        </button>
                      </div>
                    )}

                    {/* 편집 패널 */}
                    {editingMember === member.id && (
                      <div style={styles.editPanel} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.editHeader}>
                          <h4 style={styles.editTitle}>멤버 등급 관리</h4>
                          <button onClick={() => setEditingMember(null)} style={styles.closeButton}>
                            ✕
                          </button>
                        </div>
                        
                        <div style={styles.editForm}>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>등급</label>
                            <select value={selectedTier} onChange={(e) => setSelectedTier(e.target.value)} style={styles.formSelect}>
                              {GUILD_TIERS.filter(tier => tier !== "길드장").map(tier => (
                                <option key={tier} value={tier}>{tier}</option>
                              ))}
                            </select>
                          </div>
                          
                          {selectedTier === "부길드장" && (
                            <div style={styles.formGroup}>
                              <label style={styles.formLabel}>역할</label>
                              <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} style={styles.formSelect}>
                                <option value="">역할 선택</option>
                                {SUB_LEADER_ROLES.map(role => (
                                  <option key={role} value={role}>{role}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          <div style={styles.editActions}>
                            <button
                              onClick={() => handleTierChange(member.id, selectedTier, selectedRole)}
                              style={styles.saveButton}
                            >
                              ✓ 저장
                            </button>
                            <button onClick={() => handleKickMember(member)} style={styles.kickButton}>
                              🚫 강제 탈퇴
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={styles.pageButton}
                >
                  ← 이전
                </button>
                
                <div style={styles.pageNumbers}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === totalPages || 
                      Math.abs(page - currentPage) <= 2
                    )
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span style={styles.pageEllipsis}>...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          style={{
                            ...styles.pageNumber,
                            ...(currentPage === page ? styles.activePageNumber : {})
                          }}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={styles.pageButton}
                >
                  다음 →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 멤버 상세 정보 모달 */}
      {showMemberDetail && (
        <div style={styles.modalOverlay} onClick={() => setShowMemberDetail(null)}>
          <div style={styles.memberDetailModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>멤버 상세 정보</h3>
              <button onClick={() => setShowMemberDetail(null)} style={styles.modalCloseButton}>
                ✕
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.memberDetailProfile}>
                <img 
                  src={showMemberDetail.profileImg || "/default-avatar.png"}
                  alt="프로필"
                  style={styles.memberDetailAvatar}
                />
                <div style={styles.memberDetailInfo}>
                  <h3 style={styles.memberDetailName}>{showMemberDetail.nickname}</h3>
                  <div style={styles.memberDetailTier}>
                    <span style={{
                      ...styles.tierBadge,
                      backgroundColor: getTierColor(showMemberDetail.guildTier)
                    }}>
                      {getTierIcon(showMemberDetail.guildTier)} {showMemberDetail.guildTier}
                    </span>
                    {showMemberDetail.guildRole && (
                      <span style={styles.roleBadge}>
                        {showMemberDetail.guildRole}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={styles.memberDetailStats}>
                <div style={styles.detailStatCard}>
                  <div style={styles.detailStatIcon}>💎</div>
                  <div style={styles.detailStatContent}>
                    <div style={styles.detailStatValue}>{showMemberDetail.mmr || 0}</div>
                    <div style={styles.detailStatLabel}>개인 MMR</div>
                  </div>
                </div>
                
                <div style={styles.detailStatCard}>
                  <div style={styles.detailStatIcon}>⭐</div>
                  <div style={styles.detailStatContent}>
                    <div style={styles.detailStatValue}>{showMemberDetail.contribution || 0}</div>
                    <div style={styles.detailStatLabel}>기여도</div>
                  </div>
                </div>
                
                <div style={styles.detailStatCard}>
                  <div style={styles.detailStatIcon}>⚔️</div>
                  <div style={styles.detailStatContent}>
                    <div style={styles.detailStatValue}>
                      {showMemberDetail.guildWins || 0}승 {showMemberDetail.guildLosses || 0}패
                    </div>
                    <div style={styles.detailStatLabel}>길드전 기록</div>
                  </div>
                </div>
                
                <div style={styles.detailStatCard}>
                  <div style={styles.detailStatIcon}>📅</div>
                  <div style={styles.detailStatContent}>
                    <div style={styles.detailStatValue}>
                      {showMemberDetail.joinedGuildAt ? 
                        showMemberDetail.joinedGuildAt.toDate().toLocaleDateString() : 
                        "정보 없음"
                      }
                    </div>
                    <div style={styles.detailStatLabel}>가입일</div>
                  </div>
                </div>
              </div>

              <div style={styles.memberDetailActions}>
                <button
                  onClick={() => navigate(`/mypage/${showMemberDetail.id}`)}
                  style={styles.viewProfileButton}
                >
                  👤 프로필 보기
                </button>
                
                {canManage && showMemberDetail.guildTier !== "길드장" && showMemberDetail.id !== localUser.id && (
                  <>
                    <button
                      onClick={() => {
                        setShowMemberDetail(null);
                        setEditingMember(showMemberDetail.id);
                        setSelectedTier(showMemberDetail.guildTier);
                        setSelectedRole(showMemberDetail.guildRole || "");
                      }}
                      style={styles.manageButton}
                    >
                      ⚙️ 등급 관리
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowMemberDetail(null);
                        handleKickMember(showMemberDetail);
                      }}
                      style={styles.kickButtonSmall}
                    >
                      🚫 강제 탈퇴
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 단체 알림 모달 */}
      {showBulkNotification && (
        <div style={styles.modalOverlay} onClick={() => setShowBulkNotification(false)}>
          <div style={styles.notificationModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>📢 단체 알림 보내기</h3>
              <button
                onClick={() => setShowBulkNotification(false)}
                style={styles.modalCloseButton}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.notificationInfo}>
                <div style={styles.targetInfo}>
                  <div style={styles.targetIcon}>🎯</div>
                  <div style={styles.targetText}>
                    <div style={styles.targetTitle}>알림 대상</div>
                    <div style={styles.targetDesc}>
                      {selectedMembers.length > 0 ? 
                        `선택된 ${selectedMembers.length}명의 멤버` :
                        `전체 ${members.length}명의 멤버`
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.messageContainer}>
                <label style={styles.messageLabel}>메시지 내용</label>
                <textarea
                  value={bulkMessage}
                  onChange={(e) => setBulkMessage(e.target.value)}
                  placeholder="길드원들에게 전달할 메시지를 입력하세요..."
                  style={styles.messageTextarea}
                  maxLength={500}
                />
                <div style={styles.messageCounter}>
                  {bulkMessage.length}/500
                </div>
              </div>

              <div style={styles.modalActions}>
                <button
                  onClick={sendBulkNotification}
                  disabled={!bulkMessage.trim()}
                  style={{
                    ...styles.sendButton,
                    ...(bulkMessage.trim() ? {} : styles.disabledButton)
                  }}
                >
                  📤 전송하기
                </button>
                <button
                  onClick={() => {
                    setShowBulkNotification(false);
                    setBulkMessage("");
                  }}
                  style={styles.cancelButton}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 스타일 객체 (간소화)
const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#1f2937"
  },
  
  // 로딩
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff"
  },
  loadingSpinner: {
    width: 60,
    height: 60,
    border: "6px solid rgba(255,255,255,0.3)",
    borderTop: "6px solid #fff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: 24
  },
  loadingContent: { textAlign: "center" },
  loadingText: { fontSize: 20, fontWeight: 600, margin: "0 0 8px 0" },
  loadingSubtext: { fontSize: 14, opacity: 0.8 },

  // 에러
  errorContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "80vh",
    padding: 20
  },
  errorCard: {
    background: "rgba(255,255,255,0.95)",
    borderRadius: 24,
    padding: 48,
    textAlign: "center",
    maxWidth: 400,
    backdropFilter: "blur(20px)"
  },
  errorIcon: { fontSize: 64, marginBottom: 24 },
  errorTitle: { fontSize: 28, fontWeight: 700, marginBottom: 16, color: "#1f2937" },
  errorDesc: { fontSize: 16, color: "#6b7280", marginBottom: 32, lineHeight: 1.6 },

  // 헤더
  header: {
    background: "rgba(255,255,255,0.1)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255,255,255,0.2)",
    margin: 20,
    borderRadius: 24,
    padding: 32,
    color: "#fff"
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24
  },
  backButton: {
    background: "rgba(255,255,255,0.2)",
    border: "1px solid rgba(255,255,255,0.3)",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: 12,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },
  headerActions: { display: "flex", gap: 12 },
  notificationButton: {
    background: "linear-gradient(135deg, #ff6b35, #f7931e)",
    border: "none",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: 12,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "0 4px 15px rgba(255,107,53,0.4)"
  },
  headerContent: { marginBottom: 32 },
  guildInfo: { display: "flex", alignItems: "center", gap: 24 },
  guildAvatar: {
    width: 80,
    height: 80,
    borderRadius: 20,
    overflow: "hidden",
    border: "3px solid rgba(255,255,255,0.3)"
  },
  guildLogo: { width: "100%", height: "100%", objectFit: "cover" },
  guildLogoPlaceholder: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 36,
    color: "#fff"
  },
  guildDetails: { flex: 1 },
  title: { fontSize: 32, fontWeight: 800, margin: "0 0 8px 0" },
  guildMeta: { display: "flex", gap: 20, fontSize: 16, opacity: 0.9 },
  guildSport: { fontWeight: 600 },
  guildRegion: { fontWeight: 500 },
  totalMembers: { fontWeight: 600, color: "#fbbf24" },

  // 통계
  statsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 16
  },
  statCard: {
    background: "rgba(255,255,255,0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 16,
    padding: 20,
    display: "flex",
    alignItems: "center",
    gap: 16,
    cursor: "pointer",
    transition: "all 0.3s ease",
    position: "relative"
  },
  statIcon: { fontSize: 32, flexShrink: 0 },
  statContent: { flex: 1 },
  statNumber: { fontSize: 24, fontWeight: 700, lineHeight: 1 },
  statLabel: { fontSize: 14, opacity: 0.8, marginTop: 4 },
  statIndicator: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderRadius: "0 16px 16px 0"
  },

  // 컨트롤
  controlsSection: {
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0,0,0,0.1)",
    margin: "0 20px 20px 20px",
    borderRadius: 20,
    padding: 24,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 20
  },
  searchSection: { flex: 1, minWidth: 300 },
  searchInputContainer: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: {
    position: "absolute",
    left: 16,
    fontSize: 16,
    color: "#6b7280",
    zIndex: 1
  },
  searchInput: {
    width: "100%",
    padding: "14px 20px 14px 48px",
    border: "2px solid #e5e7eb",
    borderRadius: 12,
    fontSize: 16,
    background: "#fff",
    transition: "all 0.3s ease",
    boxSizing: "border-box"
  },
  clearButton: {
    position: "absolute",
    right: 12,
    background: "none",
    border: "none",
    color: "#6b7280",
    cursor: "pointer",
    padding: 4,
    borderRadius: 4
  },
  filterSection: { display: "flex", gap: 20, alignItems: "center" },
  filterGroup: { display: "flex", alignItems: "center", gap: 8 },
  filterLabel: { fontSize: 14, fontWeight: 600, color: "#374151", whiteSpace: "nowrap" },
  filterSelect: {
    padding: "10px 12px",
    border: "2px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    background: "#fff",
    color: "#374151",
    minWidth: 120
  },

  // 멤버 섹션
  membersSection: { margin: "0 20px 20px 20px" },
  membersHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 16
  },
  resultsInfo: { color: "#fff" },
  resultsText: { fontSize: 16, fontWeight: 600 },
  selectedInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "rgba(255,255,255,0.2)",
    padding: "8px 16px",
    borderRadius: 20,
    color: "#fff"
  },
  selectedText: { fontSize: 14, fontWeight: 600 },
  clearSelectionButton: {
    background: "rgba(255,255,255,0.2)",
    border: "1px solid rgba(255,255,255,0.3)",
    color: "#fff",
    padding: "4px 8px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 12
  },

  // 빈 상태
  emptyState: {
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0,0,0,0.1)",
    borderRadius: 20,
    padding: 64,
    textAlign: "center"
  },
  emptyIcon: { fontSize: 64, marginBottom: 24, opacity: 0.5 },
  emptyTitle: { fontSize: 24, fontWeight: 700, marginBottom: 12, color: "#374151" },
  emptyDesc: { fontSize: 16, color: "#6b7280", lineHeight: 1.6 },

  // 멤버 카드
  membersList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 20
  },
  memberCard: {
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0,0,0,0.1)",
    borderRadius: 20,
    padding: 24,
    transition: "all 0.3s ease",
    cursor: "pointer",
    position: "relative"
  },
  selectedMemberCard: {
    borderColor: "#4ecdc4",
    boxShadow: "0 8px 25px rgba(78,205,196,0.3)"
  },
  memberCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16
  },
  memberCheckbox: { width: 18, height: 18, cursor: "pointer" },
  onlineStatus: { fontSize: 12 },
  memberProfile: { display: "flex", alignItems: "center", gap: 16, marginBottom: 20 },
  memberAvatarContainer: { position: "relative" },
  memberAvatar: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    objectFit: "cover",
    border: "3px solid #e5e7eb"
  },
  tierBadgeSmall: {
    position: "absolute",
    bottom: -4,
    right: -4,
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    color: "#fff",
    border: "2px solid #fff"
  },
  memberInfo: { flex: 1 },
  memberName: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#1f2937"
  },
  myBadge: {
    background: "#4ecdc4",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600
  },
  memberTier: { display: "flex", alignItems: "center", gap: 8 },
  tierBadge: {
    color: "#fff",
    padding: "4px 12px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600
  },
  roleBadge: {
    background: "rgba(0,0,0,0.1)",
    color: "#374151",
    padding: "4px 8px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 500
  },
  memberStats: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    marginBottom: 16
  },
  statItem: {
    textAlign: "center",
    padding: 12,
    background: "rgba(0,0,0,0.05)",
    borderRadius: 8
  },
  statValue: { fontSize: 14, fontWeight: 600, color: "#1f2937", marginBottom: 4 },
  statLabel: { fontSize: 12, color: "#6b7280" },
  memberActions: { display: "flex", gap: 8, justifyContent: "center" },
  editButton: {
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600
  },

  // 편집 패널
  editPanel: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,0.98)",
    backdropFilter: "blur(20px)",
    borderRadius: 20,
    padding: 24,
    border: "2px solid #4ecdc4",
    zIndex: 10
  },
  editHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20
  },
  editTitle: { fontSize: 18, fontWeight: 700, color: "#1f2937", margin: 0 },
  closeButton: {
    background: "none",
    border: "none",
    color: "#6b7280",
    cursor: "pointer",
    padding: 4,
    borderRadius: 4,
    fontSize: 16
  },
  editForm: { display: "flex", flexDirection: "column", gap: 16 },
  formGroup: { display: "flex", flexDirection: "column", gap: 8 },
  formLabel: { fontSize: 14, fontWeight: 600, color: "#374151" },
  formSelect: {
    padding: "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    background: "#fff",
    color: "#374151"
  },
  editActions: { display: "flex", gap: 12, marginTop: 16 },
  saveButton: {
    flex: 1,
    background: "linear-gradient(135deg, #4ecdc4, #44a08d)",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },
  kickButton: {
    flex: 1,
    background: "linear-gradient(135deg, #ff6b6b, #ee5a52)",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },

  // 페이지네이션
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
    marginTop: 32,
    padding: 20
  },
  pageButton: {
    background: "rgba(255,255,255,0.2)",
    border: "1px solid rgba(255,255,255,0.3)",
    color: "#fff",
    padding: "10px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },
  pageNumbers: { display: "flex", gap: 8, alignItems: "center" },
  pageNumber: {
    background: "rgba(255,255,255,0.2)",
    border: "1px solid rgba(255,255,255,0.3)",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    minWidth: 40,
    textAlign: "center"
  },
  activePageNumber: { background: "#4ecdc4", borderColor: "#4ecdc4", color: "#fff" },
  pageEllipsis: { color: "rgba(255,255,255,0.6)", fontSize: 14, padding: "0 4px" },

  // 모달
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20
  },
  memberDetailModal: {
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(20px)",
    borderRadius: 24,
    padding: 32,
    maxWidth: 500,
    width: "100%",
    maxHeight: "80vh",
    overflowY: "auto"
  },
  notificationModal: {
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(20px)",
    borderRadius: 24,
    padding: 32,
    maxWidth: 600,
    width: "100%",
    maxHeight: "80vh",
    overflowY: "auto"
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: "1px solid rgba(0,0,0,0.1)"
  },
  modalTitle: { fontSize: 24, fontWeight: 700, color: "#1f2937", margin: 0 },
  modalCloseButton: {
    background: "none",
    border: "none",
    color: "#6b7280",
    cursor: "pointer",
    padding: 8,
    borderRadius: 8,
    fontSize: 20
  },
  modalContent: { color: "#1f2937" },

  // 멤버 상세
  memberDetailProfile: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    marginBottom: 32,
    padding: 24,
    background: "rgba(0,0,0,0.05)",
    borderRadius: 16
  },
  memberDetailAvatar: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    objectFit: "cover",
    border: "4px solid #e5e7eb"
  },
  memberDetailInfo: { flex: 1 },
  memberDetailName: { fontSize: 24, fontWeight: 700, marginBottom: 8, color: "#1f2937" },
  memberDetailTier: { display: "flex", alignItems: "center", gap: 12 },
  memberDetailStats: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 16,
    marginBottom: 32
  },
  detailStatCard: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: 20,
    background: "rgba(0,0,0,0.05)",
    borderRadius: 12
  },
  detailStatIcon: { fontSize: 24, flexShrink: 0 },
  detailStatContent: { flex: 1 },
  detailStatValue: { fontSize: 18, fontWeight: 700, color: "#1f2937", marginBottom: 4 },
  detailStatLabel: { fontSize: 14, color: "#6b7280" },
  memberDetailActions: { display: "flex", gap: 12, justifyContent: "center" },
  viewProfileButton: {
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },
  manageButton: {
    background: "linear-gradient(135deg, #4ecdc4, #44a08d)",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },
  kickButtonSmall: {
    background: "linear-gradient(135deg, #ff6b6b, #ee5a52)",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600
  },

  // 알림 모달
  notificationInfo: { marginBottom: 24 },
  targetInfo: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: 20,
    background: "rgba(0,0,0,0.05)",
    borderRadius: 12
  },
  targetIcon: { fontSize: 24, flexShrink: 0 },
  targetText: { flex: 1 },
  targetTitle: { fontSize: 16, fontWeight: 600, marginBottom: 4, color: "#374151" },
  targetDesc: { fontSize: 14, color: "#6b7280" },
  messageContainer: { marginBottom: 24 },
  messageLabel: {
    display: "block",
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
    color: "#374151"
  },
  messageTextarea: {
    width: "100%",
    minHeight: 120,
    padding: 16,
    border: "2px solid #e5e7eb",
    borderRadius: 12,
    fontSize: 14,
    background: "#fff",
    color: "#374151",
    resize: "vertical",
    boxSizing: "border-box",
    lineHeight: 1.5
  },
  messageCounter: { textAlign: "right", fontSize: 12, color: "#6b7280", marginTop: 8 },
  modalActions: { display: "flex", gap: 16, justifyContent: "center" },
  sendButton: {
    background: "linear-gradient(135deg, #4ecdc4, #44a08d)",
    color: "#fff",
    border: "none",
    padding: "16px 32px",
    borderRadius: 12,
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 600,
    transition: "all 0.3s ease"
  },
  disabledButton: { background: "#d1d5db", cursor: "not-allowed" },
  cancelButton: {
    background: "rgba(0,0,0,0.1)",
    color: "#374151",
    border: "1px solid rgba(0,0,0,0.2)",
    padding: "16px 32px",
    borderRadius: 12,
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 600
  },
  primaryButton: {
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    color: "#fff",
    border: "none",
    padding: "16px 32px",
    borderRadius: 12,
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 600,
    transition: "all 0.3s ease"
  }
};

export default GuildMemberManagement;